import { Component, ElementRef, HostListener, OnDestroy, OnInit, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, CanDeactivateFn, ParamMap, Router } from '@angular/router';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take } from 'rxjs/operators';
import days from 'dayjs';
import Swal from 'sweetalert2';

import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { AlertService } from '@app/shared/alert.service';
import { ValuesService } from '@app/common/values/values.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { PartnerService } from '@app/common/partner/partner.service';
import { C_value, Store, User } from '@app/shared/_models/common';
import { C_VALUES_DELIVERY_OPERATOR, C_VALUES_DELIVERY_SLOT, C_VALUES_VEHICLE, EventType, USER_ROLE_LIVREUR } from '@app/shared/constants';
import { GeoService } from '@app/shared/geo.service';
import { LogisticParamsService } from '@app/shared/logistic-params.service';
import { PrintService } from '@app/shared/print.service';
import { SpinnerService } from '@app/shared/spinner.service';
import { isoAtMinutes, isoAtWall, nowTz, todayStr } from '@app/shared/time';

import { LogisticService, DeliveryPlanning } from '../logistic.service';
import { DelRun, DelrunsService, GeoPoint, RouteVM } from './delruns.service';
import { BoardState, DelrunsSyncService } from './delruns-sync.service';
import { ScheduleResult, computeSchedule, fromHhmm } from './delrun-schedule';
import { DelrunComponent } from './delrun/delrun.component';
import { DeliveryCardComponent } from './delivery-card/delivery-card.component';
import { DelrunsMapComponent, MapPoint } from './delruns-map/delruns-map.component';

// Une tournée à l'écran : la tournée + ses livraisons ordonnées + flag « modifs non enregistrées »
export interface RunVM {
	run: DelRun;
	deliveries: DeliveryPlanning[];
	dirty: boolean;
	saving?: boolean;							// POST en cours : bloque re-Valider (double INSERT) et Supprimer
	depotStore?: number | null;		// établissement de départ (c_store) — état d'édition, figé sur run.depot à la validation (saveRun), restauré depuis la base au chargement (dispatch)
	startSlot?: number | null;		// heure de départ = créneau horaire c_values 8 — état d'édition, figé sur run.start_slot à la validation, restauré au chargement
	route?: RouteVM | null;				// temps de trajet calculés (Géoplateforme itinéraire)
	routeToken?: number;					// invalide les calculs en vol quand la tournée change
	schedule?: ScheduleResult | null;		// heures d'arrivée + diagnostics (computeSchedule) — recalculé, jamais persisté
}

export const RUN_COLORS = ['#1976d2', '#e53935', '#43a047', '#fb8c00', '#8e24aa', '#00897b', '#6d4c41', '#c0ca33'];

// Nom de la fenêtre carte dissociée : un second clic la réutilise (re-navigation + focus) au lieu
// d'empiler une fenêtre de plus à chaque fois.
const MAP_WINDOW_NAME = 'mercalink-delruns-map';

// Board de planification des tournées d'une journée :
// livraisons du jour (« À planifier ») + colonnes tournées, drag & drop, filtre Matin/Après-midi
// 3 états (re-clic = tout voir), carte OSM optionnelle. Persistance via events DELRUN.
@Component({
	selector: 'app-delruns',
	imports: [DragDropModule, MatButtonModule, MatButtonToggleModule, MatIconModule, MatProgressSpinnerModule,
		MenuCommonComponent, SelectDateComponent, DelrunComponent, DeliveryCardComponent, DelrunsMapComponent],
	templateUrl: './delruns.component.html',
	styleUrl: './delruns.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: true,
})
export class DelrunsComponent implements OnInit, OnDestroy {

	private logisticService = inject(LogisticService);
	private delrunsService = inject(DelrunsService);
	private valuesService = inject(ValuesService);
	private commonService = inject(CommonService);
	private alertService = inject(AlertService);
	private loginService = inject(LoginService);
	private partnerService = inject(PartnerService);
	private geoService = inject(GeoService);
	private logisticParams = inject(LogisticParamsService);
	private sync = inject(DelrunsSyncService);
	private printService = inject(PrintService);
	private spin = inject(SpinnerService);
	private activatedRoute = inject(ActivatedRoute);
	private router = inject(Router);
	private host: ElementRef<HTMLElement> = inject(ElementRef);
	private subscriptions = new Subscription();		// souscriptions HTTP en vol, annulées à la destruction

	public date: Date = new Date();
	public slotFilter = signal<number | null>(null);		// toggle 3 états : null = tout, 1 = Matin, 2 = Après-midi
	public highlightBl = signal<number | null>(null);		// ?bl= : BL tout juste planifié (lien de la fenêtre de livraison), en surbrillance
	public sortByRef = signal(false);		// colonne « À planifier » : tri par repère au lieu du tri par créneau
	public showMap = signal(false);
	public fullMap = false;		// ?map=full : fenêtre dissociée « carte seule » (board masqué, carte plein écran)
	public loading = signal(false);
	private printing = false;		// GET des BL en vol : le voile ne fige pas le clavier, un second Entrée ne relance rien
	public synced = signal(false);		// fenêtre carte : elle reçoit l'état du board en direct (voir setupSync)
	private mapListeners = false;			// board : une fenêtre carte écoute => publier à chaque changement

	public slots: C_value[] = [];			// c_values 8 (créneaux : data.parent / start_run / start_min)
	public ops: C_value[] = [];				// c_values 9 (opérateurs de précision)
	public vehicles: C_value[] = [];	// c_values 21 (véhicules de livraison)
	public users: User[] = [];		// tous les utilisateurs
	public deliverers: User[] = [];	// utilisateurs de rôle Livreur — liste par défaut du select « Livreurs »
	public stores: Store[] = [];	// établissements : dépôt de départ des tournées (c_store.partner porte l'adresse)
	private depotPoints = new Map<number, Observable<GeoPoint | null>>();		// coordonnées dépôt, cachées par store

	public unassigned: DeliveryPlanning[] = [];					// livraisons du jour sans tournée (triées par créneau)
	public visibleUnassigned: DeliveryPlanning[] = [];	// vue filtrée par le toggle (cdkDropListData)
	public runs: RunVM[] = [];
	public mapPoints: MapPoint[] = [];
	private mapRefs = new Map<number, string>();		// id BL -> repère de carte (voir DelrunsService.mapRefsOf)
	public countMorning = 0;
	public countAfternoon = 0;

	// Journée dont les données sont AFFICHÉES (clé YYYY-MM-DD ; null = rien de chargé => 1er chargement forcé).
	// Mémo volontairement distinct de this.date : le sélecteur de date MUTE EN PLACE l'objet qu'on lui passe
	// (SelectDateComponent.variationDate, flèches J±1), donc this.date vaut déjà la journée demandée quand
	// l'URL nous revient — il ne peut pas dire ce qui est à l'écran.
	private loadedDate: string | null = null;

	ngOnInit(): void {
		// Mode « fenêtre dissociée » : /logistic/delruns/:date/:slot?map=full (ouvert par openMapWindow)
		if (this.activatedRoute.snapshot.queryParams['map'] === 'full') {
			this.fullMap = true;
			this.showMap.set(true);
		}
		this.setupSync();
		this.subscriptions.add(this.delrunsService.blEdited$.subscribe(() => this.onBlEdited()));

		this.subscriptions.add(forkJoin({
			slots: this.valuesService.getValues$(C_VALUES_DELIVERY_SLOT).pipe(take(1)),
			ops: this.valuesService.getValues$(C_VALUES_DELIVERY_OPERATOR).pipe(take(1)),
			vehicles: this.valuesService.getValues$(C_VALUES_VEHICLE).pipe(take(1), catchError(() => of([]))),		// référentiel optionnel
			users: this.commonService.getUsers().pipe(catchError(() => of([] as User[]))),
			stores: this.commonService.getStores().pipe(catchError(() => of([] as Store[]))),
		}).subscribe({
			next: ({ slots, ops, vehicles, users, stores }) => {
				this.slots = slots ?? [];
				this.ops = ops ?? [];
				this.vehicles = ((vehicles ?? []) as any[]).filter((v: any) => v.is_active !== false);
				this.users = users ?? [];
				this.computeDeliverers();
				this.stores = stores ?? [];
				// Référentiels prêts → la journée + le créneau sont désormais pilotés par l'URL (F5-safe)
				this.subscriptions.add(this.activatedRoute.paramMap.subscribe((pm) => this.onRouteParams(pm)));
			},
			error: (e) => {
				console.error(e);
				this.alertService.error('ERREUR au chargement des paramètres de tournées');
			},
		}));
	}

	// Date + créneau lus depuis l'URL : /logistic/delruns/:date(/:slot). L'URL est la source de vérité
	// (F5 conserve la journée). Rechargement serveur seulement si la JOURNÉE change ; matin ↔ après-midi
	// (même route :date/:slot, instance conservée) = simple refiltre côté client.
	private onRouteParams(pm: ParamMap): void {
		const dateStr = pm.get('date');
		// Entrée nue /logistic/delruns : redirige vers l'URL canonique (aujourd'hui + créneau par défaut)
		if (!dateStr) {
			this.applyDefaultSlotFilter();
			this.navigateToDay(new Date(), this.slotFilter(), true);
			return;
		}
		const slotStr = pm.get('slot');		// 'matin' | 'apres-midi' | null (journée entière)
		const newDate = days(dateStr).isValid() ? days(dateStr).toDate() : new Date();
		const newSlot = slotStr === 'matin' ? 1 : slotStr === 'apres-midi' ? 2 : null;
		this.date = newDate;
		this.slotFilter.set(newSlot);
		this.highlightBl.set(Number(this.activatedRoute.snapshot.queryParams['bl']) || null);	// navigateToDay ne le reporte pas : changer de vue l'éteint
		// Journée demandée par l'URL vs journée AFFICHÉE (loadedDate, jamais this.date : voir sa déclaration)
		if (days(newDate).format('YYYY-MM-DD') !== this.loadedDate) {
			this.load();		// le dispatch recrée la tournée en attente au créneau courant
			return;
		}
		// Même journée, seul le créneau a changé (matin ↔ après-midi) : aucun rechargement, refiltre local
		this.syncDraftRunSlot();
		this.refreshLists();
	}

	// La tournée en attente suit le créneau de la PAGE. C'est dans la vue affichée qu'on la remplit :
	// restée sur « Matin » alors que le board montre l'après-midi, elle recevrait des livraisons de
	// l'après-midi puis serait enregistrée « Matin » — et disparaîtrait du filtre où on l'a remplie.
	// Le créneau n'est figé qu'à la création (createRun), et matin ↔ après-midi ne recharge pas le board.
	// Dès qu'elle porte une livraison, un véhicule ou un livreur, c'est le travail de l'utilisateur :
	// elle garde SON créneau (isPristineEmptyRun) — le toggle de la page ne décide plus pour lui.
	private syncDraftRunSlot(): void {
		this.runs
			.filter((vm) => this.isPristineEmptyRun(vm) && vm.run.slot !== this.slotFilter())
			.forEach((vm) => this.onSlotChange(vm, this.slotFilter()));		// réutilisé : il réinitialise aussi l'heure de départ hors créneau
	}

	// Livreurs (rôle Livreur) proposés par défaut dans les tournées. Tant que le back ne renvoie pas encore
	// les rôles (aucun user avec roles), on ne filtre pas (repli = tous les utilisateurs) pour ne pas vider le select.
	private computeDeliverers(): void {
		const anyRoles = this.users.some((u) => (u.roles ?? []).length > 0);
		this.deliverers = anyRoles ? this.users.filter((u) => (u.roles ?? []).includes(USER_ROLE_LIVREUR)) : this.users;
	}

	// URL canonique d'une journée + créneau (segment :slot présent seulement pour matin/après-midi).
	private navigateToDay(date: Date, slot: number | null, replaceUrl = false): void {
		const seg: any[] = ['/logistic/delruns', days(date).format('YYYY-MM-DD')];
		if (slot) seg.push(slot === 1 ? 'matin' : 'apres-midi');
		this.router.navigate(seg, { replaceUrl, queryParams: this.fullMap ? { map: 'full' } : {} });
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
		if (!this.fullMap) this.sync.bye();		// navigation interne hors du board : les cartes ne sont plus alimentées
	}

	// Fermeture / rechargement de l'onglet avec des tournées non enregistrées : confirmation native
	@HostListener('window:beforeunload', ['$event'])
	onBeforeUnload(ev: BeforeUnloadEvent): void {
		// ngOnDestroy n'est pas garanti à la fermeture d'un onglet : le « bye » part d'ici.
		// Sur F5 du board, la carte se désynchronise puis se resynchronise au « hello » du rechargement.
		if (!this.fullMap) this.sync.bye();
		if (this.dirtyCount > 0) {
			ev.preventDefault();
			ev.returnValue = true;		// requis par Chrome pour afficher la confirmation
		}
	}

	// Garde de sortie (canDeactivate) : navigation interne avec des tournées non enregistrées
	canLeave(): boolean | Promise<boolean> {
		if (!this.dirtyCount) return true;
		return Swal.fire({
			title: 'Quitter la planification ?',
			text: `${this.dirtyCount} tournée(s) modifiée(s) ne sont pas enregistrées : leurs modifications seront perdues.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Quitter sans enregistrer',
			cancelButtonText: 'Rester',
		}).then((r) => r.isConfirmed);
	}

	// Changement de journée (sélecteur de date) : confirme si des tournées non enregistrées, puis navigue
	// vers l'URL de la nouvelle journée (avec son créneau par défaut) — l'URL pilote ensuite le chargement.
	onDateChange(dat: Date): void {
		if (!this.dirtyCount) { this.navigateToDay(dat, this.defaultSlotFor(dat)); return; }		// rien à perdre : navigation directe
		this.confirmLeaveDirty().then((ok) => {
			if (ok) this.navigateToDay(dat, this.defaultSlotFor(dat));
			// Refus : le sélecteur revient sur la journée AFFICHÉE — pas sur this.date, que les flèches J±1
			// ont déjà mutée vers la journée refusée. Nouvelle référence => le sélecteur se resynchronise.
			else this.date = this.loadedDate ? days(this.loadedDate).toDate() : new Date(this.date);
		});
	}

	// Filtre créneau par défaut à l'arrivée sur une journée : passée => tout voir (journée terminée) ;
	// aujourd'hui => Matin, ou Après-midi passé pm_switch_hour (paramètre) ; future => Matin.
	private applyDefaultSlotFilter(): void {
		this.slotFilter.set(this.defaultSlotFor(this.date));
	}

	private defaultSlotFor(date: Date): number | null {
		// Jour et heure au sens MÉTIER (Europe/Paris), pas ceux du navigateur : le bon créneau
		// par défaut est celui du magasin, même depuis un poste réglé sur un autre fuseau.
		const today = todayStr();
		const viewed = days(date).format('YYYY-MM-DD');
		if (viewed < today) return null;		// journée passée : tout voir
		if (viewed > today) return 1;				// journée future : Matin
		return nowTz().hour() >= this.logisticParams.params().pm_switch_hour ? 2 : 1;
	}

	@HostListener('window:resize')
	onWindowResize(): void {
		// rien à faire : l'événement déclenche la détection de changements, le getter mapHeight est relu
	}

	// Carte agrandie : les contrôles (date, créneau, boutons) sont posés PAR-DESSUS la carte (header .over-map).
	// Board : 50 % de la hauteur de la fenêtre ; fenêtre dissociée (?map=full) : quasi plein écran.
	get mapHeight(): number {
		return this.fullMap ? Math.max(440, window.innerHeight - 90) : Math.max(360, Math.round(window.innerHeight * 0.5));
	}

	// Carte dissociée : la même page en mode carte seule (?map=full), alimentée en direct par le board
	// (DelrunsSyncService). Ouverte en FENÊTRE et non en onglet — window.open avec des features bascule
	// le navigateur en pop-up : on la pose sur un second écran, à côté du board, ce qui est toute la
	// raison de la dissocier.
	openMapWindow(): void {
		const seg: any[] = ['/logistic/delruns', days(this.date).format('YYYY-MM-DD')];
		if (this.slotFilter()) seg.push(this.slotFilter() === 1 ? 'matin' : 'apres-midi');
		const url = this.router.serializeUrl(this.router.createUrlTree(seg, { queryParams: { map: 'full' } }));
		const w = Math.min(1400, window.screen?.availWidth ?? 1400);
		const h = Math.min(900, window.screen?.availHeight ?? 900);
		const win = window.open(url, MAP_WINDOW_NAME, `popup=yes,width=${w},height=${h}`);
		// Bloquée par le navigateur : sans message, le clic ne fait strictement rien et on cherche pourquoi.
		if (!win) { this.alertService.error('Fenêtre carte bloquée par le navigateur — autorisez les pop-ups pour ce site'); return; }
		win.focus();		// déjà ouverte : la ramener devant, sinon le clic semble sans effet
	}

	// Relire la journée perdrait les tournées non enregistrées : elles passent d'abord.
	private onBlEdited(): void {
		if (!this.dirtyCount) { this.load(); return; }
		this.delrunsService.clearBlCache();
		this.alertService.warn('BL modifié : enregistrez vos tournées, puis rechargez la journée pour le voir à jour');
	}

	private loadToken = 0;		// invalide les chargements en vol (changement de journée rapide : la DERNIÈRE demande gagne)

	load(): void {
		const token = ++this.loadToken;
		this.loading.set(true);
		this.loadedDate = days(this.date).format('YYYY-MM-DD');		// journée désormais à l'écran (mémo de onRouteParams)
		this.clearBoard();		// la journée précédente disparaît AVANT l'aller-retour serveur
		this.delrunsService.clearBlCache();		// aperçus BL : données re-fetchées après rechargement du board
		this.subscriptions.add(forkJoin({
			deliveries: this.logisticService.getPlanedDeliveriesByDate(this.date),
			// GET /del_runs pas encore déployé côté back => board utilisable quand même (tournées vides)
			runs: this.delrunsService.getDelRunsByDate(this.date).pipe(catchError((e) => {
				console.warn('GET /del_runs indisponible', e);
				return of([] as DelRun[]);
			})),
		}).subscribe({
			next: ({ deliveries, runs }) => {
				if (token !== this.loadToken) return;		// réponse d'une journée précédente arrivée en retard : ignorée
				this.dispatch(deliveries ?? [], runs ?? []);
				this.loading.set(false);
				if (this.highlightBl()) setTimeout(() => this.revealHighlighted());		// après le rendu des cartes
			},
			error: (e) => {
				if (token !== this.loadToken) return;
				this.loading.set(false);
				console.error(e);
				this.alertService.error('ERREUR Serveur : ' + (e?.error ?? ''));
			},
		}));
	}

	// Vide le board à l'instant du changement de journée. Sans ça, livraisons et tournées de la journée
	// précédente restent affichées — et déplaçables — pendant tout l'aller-retour serveur : un drop y serait
	// perdu au dispatch, et les compteurs du toggle annonceraient la veille.
	private clearBoard(): void {
		this.runs = [];
		this.unassigned = [];
		this.visibleUnassigned = [];
		this.mapRefs.clear();
		this.countMorning = 0;
		this.countAfternoon = 0;
		this.setMapPoints([]);		// carte du board ET fenêtre dissociée : plus d'épingles de la veille
	}

	// Répartit les livraisons du jour : seules les « livrables » (mode 3 ASAP / 4 programmée) entrent
	// sur le board ; celles portant un id_del_run rejoignent leur tournée, triées par seq_run.
	dispatch(deliveries: DeliveryPlanning[], runs: DelRun[]): void {
		const livrables = deliveries.filter((d) => d.id > 0 && d.mode >= 3 && !!d.lnk_order?.lnk_customer?.id);
		// Repères de carte sur la journée ENTIÈRE, avant tout filtre : le toggle créneau masque des
		// épingles, il n'en renumérote aucune (voir DelrunsService.mapRefsOf).
		this.mapRefs = DelrunsService.mapRefsOf(livrables);
		this.runs = (runs ?? [])
			.map((run) => ({
				run,
				deliveries: livrables
					.filter((d) => d.id_del_run === run.id)
					.sort((a, b) => (a.seq_run ?? 0) - (b.seq_run ?? 0)),
				dirty: false,
				depotStore: run.depot ?? this.defaultDepot(),		// dépôt restauré depuis la base (sinon défaut = magasin station)
				startSlot: run.start_slot ?? null,							// heure de départ restaurée depuis la base
			}))
			.sort((a, b) => a.run.seq - b.run.seq);
		const assigned = new Set(this.runs.flatMap((vm) => vm.deliveries.map((d) => d.id)));
		this.unassigned = livrables.filter((d) => !assigned.has(d.id));
		this.ensureDraftRun();		// une tournée attend toujours, prête à recevoir un drop
		this.refreshLists();
		this.runs.forEach((vm) => this.computeRoute(vm));		// temps de trajet dès le chargement
	}

	// --- Toggle Matin / Après-midi 3 états : re-clic sur le bouton actif => désélection = tout voir ---
	// Le créneau vit dans l'URL : on navigue, le paramMap fait le refiltre (ou le rechargement si besoin).
	onSlotFilterClick(day: number): void {
		const next = this.slotFilter() === day ? null : day;
		// Ajouter/retirer le segment :slot recrée le board (nouvelle route) → confirmer si non enregistré.
		// matin ↔ après-midi (segment :slot déjà présent) ne recharge pas : aucune confirmation.
		const willReload = (this.slotFilter() === null) !== (next === null);
		if (willReload && this.dirtyCount) {
			this.confirmLeaveDirty().then((ok) => { if (ok) this.navigateToDay(this.date, next); });
		} else {
			this.navigateToDay(this.date, next);
		}
	}

	// Confirmation « modifications non enregistrées » commune aux changements de journée/créneau internes.
	private confirmLeaveDirty(): Promise<boolean> {
		if (!this.dirtyCount) return Promise.resolve(true);
		return Swal.fire({
			title: 'Changer de vue ?',
			text: `${this.dirtyCount} tournée(s) modifiée(s) ne sont pas enregistrées : leurs modifications seront perdues.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Continuer sans enregistrer',
			cancelButtonText: 'Rester',
		}).then((r) => r.isConfirmed);
	}

	// Tournées visibles : le filtre masque les tournées de l'autre créneau (celles « Journée » restent).
	// Une tournée modifiée mais pas encore enregistrée (dirty) reste visible même si son créneau ne
	// correspond plus au filtre — sinon changer son créneau la ferait disparaître en pleine édition.
	get visibleRuns(): RunVM[] {
		const f = this.slotFilter();
		return f ? this.runs.filter((vm) => vm.dirty || !vm.run.slot || vm.run.slot === f) : this.runs;
	}

	// --- Drag & drop (les listes des tournées vivent dans app-delrun, connectées par cdkDropListGroup) ---
	onDrop(event: CdkDragDrop<DeliveryPlanning[]>): void {
		const delivery: DeliveryPlanning = event.item.data;
		const targetRun = this.runs.find((vm) => vm.deliveries === event.container.data) ?? null;
		const sourceRun = this.runs.find((vm) => vm.deliveries === event.previousContainer.data) ?? null;

		if (event.previousContainer === event.container) {
			if (!targetRun) return;		// réordonner « À planifier » n'a pas de sens (tri auto par créneau)
			moveItemInArray(targetRun.deliveries, event.previousIndex, event.currentIndex);
		} else {
			// retire de la source (dans « À planifier », l'index visuel vient de la vue filtrée : retrait par identité)
			if (sourceRun) sourceRun.deliveries.splice(event.previousIndex, 1);
			else this.unassigned = this.unassigned.filter((d) => d !== delivery);
			// insère dans la cible
			if (targetRun) targetRun.deliveries.splice(event.currentIndex, 0, delivery);
			else {
				delivery.id_del_run = null;
				delivery.seq_run = null;
				this.unassigned.push(delivery);
			}
		}
		if (sourceRun) sourceRun.dirty = true;
		if (targetRun) targetRun.dirty = true;
		this.refreshLists();
		if (sourceRun) this.computeRoute(sourceRun);
		if (targetRun && targetRun !== sourceRun) this.computeRoute(targetRun);
	}

	// Resynchronise l'état dérivé : seq_run des tournées, tri + filtre « À planifier », compteurs, carte
	refreshLists(): void {
		this.runs.forEach((vm) => vm.deliveries.forEach((d, i) => {
			d.seq_run = i + 1;
			d.id_del_run = vm.run.id || null;		// tournée pas encore enregistrée (id 0) => null
		}));
		this.sortUnassigned();
		// Compteurs du toggle : TOUTES les livraisons programmées du jour pour ce demi-journée
		// (à planifier + déjà affectées à une tournée), pas seulement celles restant à planifier.
		const allDeliveries = [...this.unassigned, ...this.runs.flatMap((vm) => vm.deliveries)];
		this.countMorning = allDeliveries.filter((d) => DelrunsService.slotDayOf(d.askslot, this.slots) === 1).length;
		this.countAfternoon = allDeliveries.filter((d) => DelrunsService.slotDayOf(d.askslot, this.slots) === 2).length;
		// Une fenêtre carte écoute : reconstruire même si la carte du board est masquée, sinon elle
		// n'aurait rien à publier (cas courant — on dissocie la carte JUSTEMENT pour la masquer ici).
		if (this.showMap() || this.mapListeners) this.rebuildMapPoints();
	}

	// Repère de carte d'une livraison : la lettre lue sur l'épingle grise, badgée sur sa vignette
	// tant qu'elle n'est pas dans une tournée. Voir DelrunsService.mapRefsOf.
	refOf(d: DeliveryPlanning): string | null {
		return this.mapRefs.get(d.id) ?? null;
	}

	// Amène la carte du BL demandé (?bl=) au centre de l'écran ; absente de la vue (date ou mode changés depuis,
	// tournée d'un autre créneau masquée par le filtre…) : on le dit, plutôt qu'un lien qui semble ne rien faire.
	revealHighlighted(): void {
		const card = this.host.nativeElement.querySelector('.delivery-card.highlight');
		if (card) card.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
		else this.alertService.info(`Le BL n° ${this.highlightBl()} n'apparaît pas sur ce planning`);
	}

	// ASAP (sans créneau) d'abord, puis Matin, puis Après-midi — et à l'intérieur d'un créneau jour,
	// par HEURE réelle du créneau horaire (data.start_min / nom), pas par id (un « 8h » créé après
	// coup aurait un id plus grand que « 14h »). Fallback id pour les créneaux sans heure.
	private slotSortKey(d: DeliveryPlanning): number {
		const day = DelrunsService.slotDayOf(d.askslot, this.slots);
		const time = DelrunsService.slotTimeOf(d.askslot, this.slots);
		return day * 100000 + (time ?? (Number(d.askslot) || 0));
	}

	// Trie puis refiltre « À planifier ». Tri par repère = tri par id de BL : c'est de lui que les
	// lettres tirent leur rang (mapRefsOf), la colonne se lit donc A, B, C… avec les trous des
	// livraisons déjà planifiées.
	// L'id départage AUSSI le tri par créneau : les livraisons d'un créneau vague ont toutes la même
	// clé, et un tri stable les laisserait dans l'ordre d'arrivée du serveur — l'ordre de la colonne
	// changerait alors à chaque bascule du toggle, et différerait d'une fenêtre à l'autre.
	private sortUnassigned(): void {
		const byRef = this.sortByRef();
		this.unassigned.sort((a, b) => (byRef ? 0 : this.slotSortKey(a) - this.slotSortKey(b)) || a.id - b.id);
		this.visibleUnassigned = this.unassigned.filter((d) => DelrunsService.matchesSlotFilter(d, this.slotFilter(), this.slots));
	}

	// Bascule le tri de la colonne. Par créneau (ASAP → matin → après-midi) = l'ordre de travail ;
	// par repère = l'ordre des lettres de la carte, pour retrouver d'un coup d'oeil les épingles
	// repérées sur un même secteur. Pas de refreshLists : l'ordre de la colonne ne concerne ni les
	// tournées ni la carte — la reconstruire recadrerait la vue pour rien.
	toggleSortByRef(): void {
		this.sortByRef.set(!this.sortByRef());
		this.sortUnassigned();
	}

	// --- Temps de trajet (Géoplateforme itinéraire — voir docs/geolocalisation-adresses.md) ---

	private defaultDepot(): number | null {
		return this.loginService.currentstation?.store ?? this.stores[0]?.id ?? null;
	}

	onDepotChange(vm: RunVM, storeId: number | null): void {
		vm.depotStore = storeId;
		this.markDirty(vm);		// dépôt désormais persisté → active « Valider »
		this.computeRoute(vm);
	}

	onStartSlotChange(vm: RunVM, slotId: number | null): void {
		vm.startSlot = slotId;
		this.markDirty(vm);		// heure de départ désormais persistée → active « Valider »
		this.applySchedule(vm);
	}

	// Créneau d'une tournée changé (toggle Matin / Après-midi / Journée) : l'heure de départ choisie
	// peut ne plus appartenir au nouveau créneau => on la réinitialise le cas échéant, puis dirty.
	onSlotChange(vm: RunVM, slot: number | null): void {
		vm.run.slot = slot;
		if (vm.startSlot != null && !DelrunsService.hourSlotsFor(this.slots, slot).some((s) => s.id === vm.startSlot)) {
			vm.startSlot = null;
			this.applySchedule(vm);
		}
		this.markDirty(vm);
	}

	// Temps sur place modifié sur une carte : arrivées à recalculer, tournée à re-valider (persistance)
	onDurationsChange(vm: RunVM): void {
		this.markDirty(vm);
		this.applySchedule(vm);
	}

	// Heure d'arrivée fixée à la main sur une carte (« HH:mm ») : pose une CONTRAINTE (lockdatdel).
	// Les livraisons suivantes se décalent, la tournée devient modifiée. Champ vidé => on délie.
	// Rien n'est écrit dans plandatdel ici : l'engagement se fige à la validation, pas à la saisie.
	onArrivalChange(vm: RunVM, index: number, value: string): void {
		const d = vm.deliveries[index];
		if (!d) return;
		const min = fromHhmm(value);
		if (min === null) { this.onArrivalClear(vm, index); return; }
		d.lockdatdel = isoAtWall(vm.run.dat, min);		// heure MURALE saisie => RFC3339 avec offset métier
		this.markDirty(vm);
		this.applySchedule(vm);
	}

	// Croix « délier » : la contrainte est retirée, l'heure repasse en calcul automatique.
	onArrivalClear(vm: RunVM, index: number): void {
		const d = vm.deliveries[index];
		if (!d || !d.lockdatdel) return;
		d.lockdatdel = null;
		this.markDirty(vm);
		this.applySchedule(vm);
	}

	// Recalcule les heures d'arrivée + les diagnostics de la tournée. TOUJOURS un calcul : `plandatdel`
	// (l'engagement enregistré) n'entre jamais ici, sinon l'engagement d'hier deviendrait la contrainte
	// d'aujourd'hui. Il n'est relu que pour l'AFFICHAGE, et seulement quand le calcul est indisponible
	// (état « committed » côté carte) — jamais mélangé en silence avec des trajets fraîchement calculés.
	private applySchedule(vm: RunVM): void {
		const params = this.logisticParams.params();
		const legs = vm.route?.legs ?? vm.deliveries.map(() => null);
		vm.schedule = computeSchedule({
			startMin: DelrunsService.slotTimeOf(vm.startSlot ?? null, this.slots),
			slackS: params.slack_s,
			defaultDurationS: params.default_duration_s,
			exactToleranceMin: params.ask_exact_tolerance_min,
			stops: vm.deliveries.map((d, i) => {
				const lock = DelrunsService.lockOf(d, vm.run.dat);
				return {
					legS: legs[i] ?? null,
					durationS: d.duration ?? null,
					lockMin: lock.min,
					lockFromOtherDay: lock.otherDay,
					askWindow: DelrunsService.askWindowOf(d, this.ops, this.slots),
				};
			}),
		});
	}

	// À l'enregistrement : fige l'ENGAGEMENT de chaque livraison dans plandatdel — l'heure retenue
	// (contrainte ou calcul) au moment de la validation. C'est l'heure qu'on communique et qu'on
	// archive ; une arrivée inconnue vaut NULL (on n'engage rien qu'on ne sache pas tenir).
	// La contrainte (lockdatdel), elle, est déjà à jour : elle n'est pas touchée ici.
	private commitSchedule(vm: RunVM): void {
		const stops = vm.schedule?.stops ?? [];
		vm.deliveries.forEach((d, i) => {
			// minutes du moteur (>= 1440 = J+1) => RFC3339 avec offset métier
			d.plandatdel = isoAtMinutes(vm.run.dat, stops[i]?.arrivalMin ?? null);
		});
	}

	// Coordonnées du dépôt d'un établissement : cufs Latitude/Longitude du partner du store (c_store.partner),
	// sinon géocodage BAN de son adresse. Cache par store : un seul GET partner par session du board.
	private depotPoint$(storeId: number | null): Observable<GeoPoint | null> {
		if (!storeId) return of(null);
		let obs$ = this.depotPoints.get(storeId);
		if (!obs$) {
			const store = this.stores.find((s) => s.id === storeId);
			if (!store?.partner) return of(null);
			obs$ = this.partnerService.getPartnerByid(store.partner).pipe(
				map((ps: any) => (Array.isArray(ps) ? ps[0] : ps) ?? null),
				switchMap((p: any) => {
					const c = DelrunsService.coordsOf(p);
					if (c) return of(c);
					const q = GeoService.geoQueryOf(p);
					return GeoService.isEmptyQuery(q) ? of(null) : this.delrunsService.geocode$(q);
				}),
				catchError(() => of(null)),
				shareReplay(1),
			);
			this.depotPoints.set(storeId, obs$);
		}
		return obs$;
	}

	private deliveryPoint$(d: DeliveryPlanning): Observable<GeoPoint | null> {
		const addr = DelrunsService.deliveryAddressOf(d);
		const c = DelrunsService.coordsOf(addr);		// cufs Latitude/Longitude : position exacte en base, prioritaire
		if (c) return of(c);
		const q = GeoService.geoQueryOf(addr);
		return GeoService.isEmptyQuery(q) ? of(null) : this.delrunsService.geocode$(q);
	}

	// Temps de trajet d'une tournée : dépôt -> livraisons dans l'ordre. UNE requête itinéraire sur les
	// points géolocalisables ; une livraison sans coordonnées a un tronçon null et sort du total (missing).
	computeRoute(vm: RunVM): void {
		if (!vm.deliveries.length) { vm.route = null; vm.schedule = null; return; }
		const token = (vm.routeToken = (vm.routeToken ?? 0) + 1);
		vm.route = { pending: true, duration: null, distance: null, legs: vm.deliveries.map(() => null), missing: 0 };
		this.applySchedule(vm);		// sans attendre le réseau : contraintes et engagements enregistrés s'affichent tout de suite
		this.subscriptions.add(forkJoin([this.depotPoint$(vm.depotStore ?? null), ...vm.deliveries.map((d) => this.deliveryPoint$(d))])
			.subscribe((pts) => {
				if (token !== vm.routeToken) return;		// la tournée a changé entre-temps : calcul obsolète
				const valid: { p: GeoPoint; i: number }[] = [];
				pts.forEach((p, i) => { if (p) valid.push({ p, i }); });
				const missing = pts.length - valid.length;
				const legs: (number | null)[] = vm.deliveries.map(() => null);
				if (valid.length < 2) {
					vm.route = { pending: false, duration: null, distance: null, legs, missing };
					this.applySchedule(vm);
					return;
				}
				this.subscriptions.add(this.geoService.route$(valid.map((x) => x.p)).subscribe((r) => {
					if (token !== vm.routeToken) return;
					if (r) for (let k = 0; k < r.legs.length; k++) {
						const target = valid[k + 1].i;		// tronçon k : arrive au point valide k+1 (index 0 = dépôt)
						if (target >= 1) legs[target - 1] = r.legs[k].duration;
					}
					vm.route = { pending: false, duration: r?.duration ?? null, distance: r?.distance ?? null, legs, missing };
					this.applySchedule(vm);
				}));
			}));
	}

	// --- Tournées ---
	createRun(): void {
		const seq = this.runs.length ? Math.max(...this.runs.map((vm) => vm.run.seq)) + 1 : 1;
		const run = new DelRun();
		run.seq = seq;
		run.dat = days(this.date).format('YYYY-MM-DD');
		run.slot = this.slotFilter();		// créneau du filtre courant par défaut
		this.runs.push({ run, deliveries: [], dirty: true, depotStore: this.defaultDepot(), startSlot: null });
	}

	// Invariant du board : il y a TOUJOURS une tournée en attente, prête à recevoir un drop — sinon
	// « + Nouvelle tournée » serait un clic obligatoire avant chaque tournée. Une tournée jamais
	// enregistrée (id 0) tient ce rôle : tant qu'il en reste une, on n'en ajoute pas d'autre — celle
	// qu'on est en train de remplir EST la tournée en attente. Appelée à l'ouverture d'une journée,
	// après chaque enregistrement et après chaque suppression.
	private ensureDraftRun(): void {
		if (!this.runs.some((vm) => !vm.run.id)) this.createRun();
	}

	markDirty(vm: RunVM): void {
		vm.dirty = true;
	}

	colorOf(vm: RunVM): string {
		return RUN_COLORS[Math.max(this.runs.indexOf(vm), 0) % RUN_COLORS.length];
	}

	// Tournée jamais enregistrée et restée vide (créée par défaut à l'ouverture d'une journée, ou via
	// « Nouvelle tournée » mais non remplie) : rien à perdre → exclue du décompte des modifications non
	// enregistrées, donc pas de pop-up « non sauvegardé » injustifiée au changement de journée / F5 / fermeture.
	private isPristineEmptyRun(vm: RunVM): boolean {
		return !vm.run.id && !vm.deliveries.length && !vm.run.vehicle && !(vm.run.workers?.length);
	}

	get dirtyCount(): number {
		return this.runs.filter((vm) => vm.dirty && !this.isPristineEmptyRun(vm)).length;
	}

	// « Valider » : INSERT (tournée jamais enregistrée) ou UPDATE, avec les livraisons ordonnées.
	// Garde vm.saving : un double-clic pendant le POST provoquerait un double INSERT (tournée dupliquée).
	saveRun(vm: RunVM): void {
		if (vm.saving) return;
		vm.saving = true;
		vm.run.depot = vm.depotStore ?? null;			// fige les choix UI (dépôt / heure de départ) pour la persistance
		vm.run.start_slot = vm.startSlot ?? null;
		this.commitSchedule(vm);		// fige l'engagement (plandatdel) de chaque livraison avant l'envoi
		const typ = vm.run.id ? EventType.UPDATE : EventType.INSERT;
		this.subscriptions.add(this.delrunsService.postDelRunEvent(typ, DelrunsService.buildRunPayload(vm.run, vm.deliveries)).subscribe({
			next: (res: any) => {
				vm.saving = false;
				if (!vm.run.id) vm.run.id = res?.data?.id ?? res?.id ?? 0;		// contrat : l'INSERT renvoie l'id créé
				vm.deliveries.forEach((d, i) => {
					d.id_del_run = vm.run.id || null;
					d.seq_run = i + 1;
				});
				vm.dirty = false;
				this.ensureDraftRun();		// celle-ci n'est plus « en cours » : la suivante attend déjà, sans clic
				this.alertService.success(`Tournée ${vm.run.seq} enregistrée`);
			},
			error: (e) => {
				vm.saving = false;
				console.error(e);
				this.alertService.error(`ERREUR Serveur : la tournée ${vm.run.seq} n'a pas été enregistrée`);
			},
		}));
	}

	saveAll(): void {
		this.runs.filter((vm) => vm.dirty && !this.isPristineEmptyRun(vm)).forEach((vm) => this.saveRun(vm));
	}

	// Bouton corbeille : confirmation avant de supprimer une tournée ENREGISTRÉE (event DELETE en base).
	// Une tournée jamais enregistrée est retirée sans confirmation : ses livraisons reviennent « À planifier ».
	confirmDeleteRun(vm: RunVM): void {
		if (!vm.run.id) { this.deleteRun(vm); return; }
		Swal.fire({
			title: `Supprimer la tournée ${vm.run.seq} ?`,
			text: 'La tournée sera supprimée et ses livraisons remises à planifier.',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: 'Oui, supprimer',
			cancelButtonText: 'Annuler',
		}).then((result) => {
			if (result.isConfirmed) this.deleteRun(vm);
		});
	}

	deleteRun(vm: RunVM): void {
		if (vm.saving) return;		// POST Valider en cours : ne pas supprimer en parallèle
		const unassignAll = () => {
			vm.deliveries.forEach((d) => {
				d.id_del_run = null;
				d.seq_run = null;
			});
			this.unassigned.push(...vm.deliveries);
			this.runs = this.runs.filter((x) => x !== vm);
			this.ensureDraftRun();		// supprimer la dernière tournée en attente en présente une neuve
			this.refreshLists();
		};
		if (!vm.run.id) {		// tournée jamais enregistrée : suppression locale
			unassignAll();
			return;
		}
		this.subscriptions.add(this.delrunsService.postDelRunEvent(EventType.DELETE, DelrunsService.buildRunPayload(vm.run, [])).subscribe({
			next: () => {
				unassignAll();
				this.alertService.success('Tournée supprimée — livraisons remises à planifier');
			},
			error: (e) => {
				console.error(e);
				this.alertService.error("ERREUR Serveur : la tournée n'a pas été supprimée");
			},
		}));
	}

	// Imprimante d'une tournée : ses BL en un seul PDF, dans l'ordre enregistré en base (d'où le bouton éteint tant que la tournée est modifiée).
	printRunBl(vm: RunVM): void {
		if (!this.printable(vm) || !vm.deliveries.length) return;
		this.printRuns([vm], `BL de la tournée ${vm.run.seq}…`);
	}

	// Tournées que l'imprimante du haut enverrait : celles AFFICHÉES (filtre créneau compris), dans l'ordre des colonnes, sans les vides.
	get runsToPrint(): RunVM[] {
		return this.visibleRuns.filter((vm) => vm.deliveries.length);
	}

	// Une tournée non validée bloque tout : le serveur imprime l'ordre enregistré, le papier différerait de l'écran.
	get runsNotSaved(): RunVM[] {
		return this.runsToPrint.filter((vm) => !this.printable(vm));
	}

	get printAllTitle(): string {
		const runs = this.runsToPrint;
		if (!runs.length) return 'Aucune tournée à imprimer';
		if (this.runsNotSaved.length) return `Validez d'abord ${this.runsNotSaved.map((vm) => 'T' + vm.run.seq).join(', ')} : les BL s'impriment dans l'ordre enregistré`;
		const nb = runs.reduce((n, vm) => n + vm.deliveries.length, 0);
		return `Imprimer les BL des ${runs.length} tournée(s) affichée(s) (${nb} BL), tournée après tournée, chacune dans son ordre de passage`;
	}

	// Imprimante du haut : toutes les tournées affichées en un seul PDF, la 1re colonne d'abord.
	printAllRuns(): void {
		if (!this.runsToPrint.length || this.runsNotSaved.length) return;
		const nb = this.runsToPrint.reduce((n, vm) => n + vm.deliveries.length, 0);
		this.printRuns(this.runsToPrint, `BL de ${this.runsToPrint.length} tournée(s), ${nb} BL…`);
	}

	private printable(vm: RunVM): boolean {
		return !!vm.run.id && !vm.dirty && !vm.saving;
	}

	// Le serveur reçoit les BL affichés et refuse (409) s'ils diffèrent des siens : livraison reportée, autre poste, glisser-déposer pendant « Valider ».
	private printRuns(vms: RunVM[], message: string): void {
		if (this.printing) return;
		this.printing = true;
		const runs = vms.map((vm) => ({ id: vm.run.id, ids: vm.deliveries.map((d) => d.id) }));
		this.subscriptions.add(this.delrunsService.getRunsBl$(runs).pipe(this.spin.block(message), finalize(() => this.printing = false)).subscribe({
			next: (blob) => this.printService.printPdf(blob),
			error: (e) => {
				console.error(e);
				// Corps d'erreur en Blob (responseType 'blob') : c'est lui qui distingue « aucun BL » d'une route absente (404 nu).
				const motif: Promise<string> = e.error instanceof Blob ? e.error.text().catch(() => '') : Promise.resolve('');
				motif.then((t) => {
					if (t) console.error(t);
					this.alertService.error(this.printRunsError(vms, e.status, t));
				});
			},
		}));
	}

	private printRunsError(vms: RunVM[], status: number, motif: string): string {
		let body: any = null;
		try { body = JSON.parse(motif); } catch { /* texte brut ou vide */ }
		const seqs = vms.map((vm) => vm.run.seq).join(', ');
		if (status === 409 && body?.error === 'screen_differs') {
			const ecarts = (body.runs ?? []).map((r: { id: number; ids?: number[] }) => {
				const vm = vms.find((x) => x.run.id === r.id);
				const ecran = vm?.deliveries.map((d) => d.id) ?? [];
				const base = r.ids ?? [];
				// Le serveur compare la liste ORDONNÉE : à nombre égal, dire ce qui diffère plutôt que « 5 à l'écran, 5 en base ».
				const quoi = ecran.length !== base.length ? `${ecran.length} BL à l'écran, ${base.length} en base`
					: [...ecran].sort().join() === [...base].sort().join() ? 'ordre de passage différent' : 'BL différents';
				return `T${vm?.run.seq ?? r.id} : ${quoi}`;
			});
			return `Écran différent de la base (${ecarts.join(' ; ')}) : rechargez la page avant d'imprimer`;
		}
		if (status === 404 && body?.error === 'no_bl') return vms.length > 1 ? `Les tournées ${seqs} n'ont aucun BL à imprimer` : `La tournée ${seqs} n'a aucun BL à imprimer`;
		return `ERREUR Serveur : les BL ${vms.length > 1 ? 'des tournées ' + seqs : 'de la tournée ' + seqs} n'ont pas été générés`;
	}

	// ============================================================
	// Synchro board <-> fenêtre carte dissociée (BroadcastChannel — voir DelrunsSyncService)
	// ============================================================

	// Sens unique : le board publie ses points, la fenêtre carte les reflète — drag & drop compris,
	// avant même l'enregistrement. Sans ça la carte est un instantané figé de la base.
	private setupSync(): void {
		if (this.fullMap) {
			this.subscriptions.add(this.sync.state$().subscribe((s) => this.applyBoardState(s)));
			this.subscriptions.add(this.sync.hellos$().subscribe(() => this.sync.request()));		// board (ré)ouvert : réclamer son état
			this.subscriptions.add(this.sync.byes$().subscribe(() => this.synced.set(false)));		// board fermé : redevient un instantané
			// Pas de request() ici : la vue (journée / créneau) n'est pas encore lue depuis l'URL et
			// l'état reçu serait comparé à la mauvaise. rebuildMapPoints s'en charge, une fois chargé.
		} else {
			this.subscriptions.add(this.sync.requests$().subscribe(() => {
				this.mapListeners = true;
				this.rebuildMapPoints();		// publie via setMapPoints — la carte du board peut être masquée
			}));
			this.sync.hello();
		}
	}

	// Fenêtre carte : adopte l'état du board, à condition qu'il regarde la MÊME vue — sinon les
	// épingles contrediraient le toggle affiché juste au-dessus. Les deux fenêtres re-convergent
	// d'elles-mêmes dès qu'elles reviennent sur la même journée et le même créneau.
	private applyBoardState(s: BoardState): void {
		const sameView = s.dat === days(this.date).format('YYYY-MM-DD') && (s.slot ?? null) === this.slotFilter();
		if (!sameView) {
			this.synced.set(false);		// le board regarde ailleurs : cette carte n'est plus alimentée, le témoin doit le dire
			return;
		}
		this.mapPoints = s.points ?? [];		// nouvelle référence : annule aussi l'ajout de dépôts en vol
		this.synced.set(true);
	}

	private publishBoardState(): void {
		this.sync.publish({ dat: days(this.date).format('YYYY-MM-DD'), slot: this.slotFilter(), points: this.mapPoints });
	}

	// Toute écriture des points passe par ici : le board en profite pour alimenter les fenêtres carte.
	private setMapPoints(pts: MapPoint[]): void {
		this.mapPoints = pts;
		if (!this.fullMap && this.mapListeners) this.publishBoardState();
	}

	// --- Carte (feature secondaire, cachée par défaut) ---
	toggleMap(): void {
		this.showMap.set(!this.showMap());
		if (this.showMap()) this.rebuildMapPoints();
	}

	rebuildMapPoints(): void {
		const pts: MapPoint[] = [];
		const push = (d: DeliveryPlanning, color: string, seq: number | null, prefix: string, ref: string | null = null) => {
			const addr = DelrunsService.deliveryAddressOf(d);
			const query = GeoService.geoQueryOf(addr);		// trois lignes de rue recollées et nettoyées (voir GeoService.geoQueryOf)
			const coords = DelrunsService.coordsOf(addr);	// cufs Latitude/Longitude : position exacte en base => prioritaire sur BAN
			if (GeoService.isEmptyQuery(query) && !coords) return;
			pts.push({
				address: query,
				lat: coords?.lat,
				lon: coords?.lon,
				label: prefix + '#' + d.id_order + ' ' + DelrunsService.customerNameOf(d),
				color,
				seq,
				ref,		// non affectée : la lettre remplace le n° d'arrêt (voir DelrunsMapComponent.pinTextOf)
				delivery: d,		// pour l'aperçu BL au survol de l'épingle
			});
		};
		// La carte suit le filtre créneau du board : tournées masquées => épingles masquées (cohérence board/carte)
		this.visibleUnassigned.forEach((d) => push(d, '#9e9e9e', null, '', this.refOf(d)));
		this.visibleRuns.forEach((vm) => vm.deliveries.forEach((d, i) => push(d, this.colorOf(vm), i + 1, `T${vm.run.seq} — `)));
		this.setMapPoints(pts);
		this.appendDepotPoints(pts);
		// Fenêtre carte : elle vient de reconstruire depuis SA copie de la base, qui ignore les
		// affectations non enregistrées. Le board republie et son état prime (s'il regarde la même vue).
		if (this.fullMap) this.sync.request();
	}

	// Épingles de départ : noires, n° 0, une par dépôt DISTINCT des tournées (plusieurs tournées
	// partent souvent du même établissement). Résolution async (cufs du partner du store sinon BAN),
	// puis nouvelle référence mapPoints => la carte se met à jour.
	private appendDepotPoints(pts: MapPoint[]): void {
		const storeIds = [...new Set(this.visibleRuns.map((vm) => vm.depotStore).filter((s): s is number => !!s))];
		if (!storeIds.length) return;
		this.subscriptions.add(forkJoin(storeIds.map((id) => this.depotPoint$(id).pipe(take(1)))).subscribe((points) => {
			if (this.mapPoints !== pts) return;		// la carte a été reconstruite entre-temps
			const depots: MapPoint[] = [];
			points.forEach((p, i) => {
				if (!p) return;
				const store = this.stores.find((s) => s.id === storeIds[i]);
				depots.push({ address: GeoService.emptyQuery(), lat: p.lat, lon: p.lon, label: 'Départ — ' + (store?.name ?? ''), color: '#212121', seq: 0 });
			});
			if (depots.length) this.setMapPoints([...depots, ...pts]);
		}));
	}
}

// Garde de navigation du board (routes /logistic/delruns/**) : bloque la sortie si des tournées
// modifiées ne sont pas enregistrées (confirmation Swal via DelrunsComponent.canLeave).
// Les changements internes de journée/créneau (URL restant sous /logistic/delruns) sont déjà confirmés
// par onDateChange / onSlotFilterClick — on les laisse passer pour éviter une double pop-up.
export const delrunsCanDeactivate: CanDeactivateFn<DelrunsComponent> = (component, _curr, _state, nextState) => {
	if (nextState?.url?.split('?')[0].startsWith('/logistic/delruns')) return true;
	return component.canLeave();
};
